// ============================================
// CREATE/EDIT RANK MODAL
// ============================================

import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { adminApi } from '@/services/api';
import { cn } from '@/utils';

interface Rank {
  id: string;
  code: string;
  nameEn: string;
  nameTr: string;
  department: string;
  level: number;
  description?: string | null;
  sortOrder: number;
  isActive: boolean;
}

interface RankModalProps {
  open: boolean;
  onClose: () => void;
  editData?: Rank | null;
}

export function RankModal({ open, onClose, editData }: RankModalProps) {
  const queryClient = useQueryClient();
  const isEdit = !!editData;

  const [formData, setFormData] = useState({
    code: '',
    nameEn: '',
    nameTr: '',
    department: 'DECK',
    level: 1,
    description: '',
    sortOrder: 0,
    isActive: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (editData) {
      setFormData({
        code: editData.code || '',
        nameEn: editData.nameEn || '',
        nameTr: editData.nameTr || '',
        department: editData.department || 'DECK',
        level: editData.level || 1,
        description: editData.description || '',
        sortOrder: editData.sortOrder || 0,
        isActive: editData.isActive !== false,
      });
    } else {
      setFormData({
        code: '',
        nameEn: '',
        nameTr: '',
        department: 'DECK',
        level: 1,
        description: '',
        sortOrder: 0,
        isActive: true,
      });
    }
    setErrors({});
  }, [editData, open]);

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => adminApi.createRank(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ranks'] });
      onClose();
    },
    onError: (error: any) => {
      setErrors({ submit: error?.response?.data?.message || 'Bir hata oluştu' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: typeof formData) => adminApi.updateRank(editData!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ranks'] });
      onClose();
    },
    onError: (error: any) => {
      setErrors({ submit: error?.response?.data?.message || 'Bir hata oluştu' });
    },
  });

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.code) newErrors.code = 'Kod zorunludur';
    if (!formData.nameEn) newErrors.nameEn = 'İngilizce ad zorunludur';
    if (!formData.nameTr) newErrors.nameTr = 'Türkçe ad zorunludur';
    if (!formData.level || formData.level < 1) newErrors.level = 'Seviye en az 1 olmalıdır';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      if (isEdit) {
        updateMutation.mutate(formData);
      } else {
        createMutation.mutate(formData);
      }
    }
  };

  if (!open) return null;

  const departmentOptions = [
    { value: 'DECK', label: 'Güverte' },
    { value: 'ENGINE', label: 'Makine' },
    { value: 'ELECTRICAL', label: 'Elektrik' },
    { value: 'CATERING', label: 'Catering' },
    { value: 'OTHER', label: 'Diğer' },
  ];

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-lg bg-white rounded-maritime shadow-maritime-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {isEdit ? 'Rütbe Düzenle' : 'Yeni Rütbe'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {isEdit ? 'Rütbe bilgilerini güncelleyin' : 'Yeni rütbe oluşturun'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Kod */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kod <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="MASTER, CE, 2O, vb."
                className={cn(
                  'w-full px-3 py-2 border rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500',
                  errors.code ? 'border-red-500' : 'border-gray-300'
                )}
              />
              {errors.code && <p className="text-xs text-red-500 mt-1">{errors.code}</p>}
            </div>

            {/* Departman */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Departman</label>
              <select
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500"
              >
                {departmentOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* İngilizce Ad */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                İngilizce Ad <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.nameEn}
                onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
                placeholder="Master, Chief Engineer, vb."
                className={cn(
                  'w-full px-3 py-2 border rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500',
                  errors.nameEn ? 'border-red-500' : 'border-gray-300'
                )}
              />
              {errors.nameEn && <p className="text-xs text-red-500 mt-1">{errors.nameEn}</p>}
            </div>

            {/* Türkçe Ad */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Türkçe Ad <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.nameTr}
                onChange={(e) => setFormData({ ...formData, nameTr: e.target.value })}
                placeholder="Kaptan, Başmühendis, vb."
                className={cn(
                  'w-full px-3 py-2 border rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500',
                  errors.nameTr ? 'border-red-500' : 'border-gray-300'
                )}
              />
              {errors.nameTr && <p className="text-xs text-red-500 mt-1">{errors.nameTr}</p>}
            </div>

            {/* Seviye */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Seviye <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                value={formData.level}
                onChange={(e) => setFormData({ ...formData, level: parseInt(e.target.value) || 1 })}
                className={cn(
                  'w-full px-3 py-2 border rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500',
                  errors.level ? 'border-red-500' : 'border-gray-300'
                )}
              />
              {errors.level && <p className="text-xs text-red-500 mt-1">{errors.level}</p>}
            </div>

            {/* Sıralama */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sıralama</label>
              <input
                type="number"
                min="0"
                value={formData.sortOrder}
                onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500"
              />
            </div>

            {/* Açıklama */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                placeholder="Rütbe açıklaması (opsiyonel)"
                className="w-full px-3 py-2 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500"
              />
            </div>

            {/* Aktif */}
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="rounded border-gray-300 text-maritime-600 focus:ring-maritime-500"
                />
                <span className="text-sm text-gray-700">Aktif</span>
              </label>
            </div>
          </div>

          {errors.submit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-maritime">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-maritime"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-maritime-600 hover:bg-maritime-700 rounded-maritime disabled:opacity-50"
            >
              {isPending ? 'Kaydediliyor...' : isEdit ? 'Güncelle' : 'Oluştur'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
